import { useQueryClient } from '@tanstack/react-query';

import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { changePortalProjectStatus, editPortalProject } from '@api/projectManagement';

import { IEditProjectDTO, IEditProjectStatusDTO } from '@interfaces/portal/dto/projectManagementDTO';

import { ESnackbarTypes } from '@enums/common';

import {
	GET_PORTAL_PROJECT,
	GET_PORTAL_PROJECTS_LIST,
	PORTAL_AVAILABLE_PROJECTS,
	UPDATE_PORTAL_PROJECT,
	UPDATE_PORTAL_PROJECT_STATUS,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

export interface IMutatePortalProjectsReturned {
	updateProjectStatus: (data: IEditProjectStatusDTO) => Promise<void>;
	isUpdatingProjectStatus: boolean;
	updatePortalProject: (data: IEditProjectDTO) => Promise<void>;
	isUpdatingPortalProject: boolean;
}

export function useMutatePortalProjects(): IMutatePortalProjectsReturned {
	const queryClient = useQueryClient();
	const dispatch = useAppDispatch();

	const {
		mutateAsync: updateProjectStatus,
		isLoading: isUpdatingProjectStatus,
	} = useMutation<IEditProjectStatusDTO>({
		mutationFn: changePortalProjectStatus,
		mutationKey: [UPDATE_PORTAL_PROJECT_STATUS],
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.statusChanged,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([GET_PORTAL_PROJECTS_LIST]),
				queryClient.invalidateQueries([PORTAL_AVAILABLE_PROJECTS]),
				queryClient.invalidateQueries([GET_PORTAL_PROJECT]),
			];
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	const {
		mutateAsync: updatePortalProject,
		isLoading: isUpdatingPortalProject,
	} = useMutation<IEditProjectDTO>({
		mutationFn: editPortalProject,
		mutationKey: [UPDATE_PORTAL_PROJECT],
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.updateProjectSuccess,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([GET_PORTAL_PROJECTS_LIST]),
				queryClient.invalidateQueries([GET_PORTAL_PROJECT]),
			];
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	return {
		updateProjectStatus,
		isUpdatingProjectStatus,
		updatePortalProject,
		isUpdatingPortalProject,
	};
}
